import React, { useState, useEffect, useRef, useCallback } from "react";
import * as SubframeCore from "@subframe/core";
import {
  DialogLayout,
  IconWithBackground,
  Button,
  Badge,
  DropdownMenu,
  Checkbox,
} from "../ui";
import {
  FeatherCreditCard,
  FeatherCalendar,
  FeatherActivity,
  FeatherChevronDown,
  FeatherDollarSign,
  FeatherBuilding,
  FeatherPlus,
  FeatherLoader,
} from "@subframe/core";
import {
  Transaction,
  BankAccount,
  categorizeVendorTransfer,
  categorizeOwnerDrawing,
  categorizeCustomerPaymentRefund,
  categorizeVendorCardPayment,
  categorizeVendorTransferToOtherAccounts,
  categorizeVendorTransferToOtherAccountsSplit,
  CreditCardAccount,
  fetchCreditCardAccounts,
  fetchTransactionActivityLogs,
  TransactionActivityLog,
  journalsAPI,
} from "../services/api";
import {
  categorizeVendorTransaction,
  categorizeMultipleVendorsTransaction,
  categorizeVendorExpense,
} from "../services/vendorApi";
import { VendorBill } from "../types";
import VendorPaymentReco from "./VendorPaymentReco";
import VendorTransferToAccountReco from "./VendorTransferToAccountReco";
import VendorTransferToOtherAccountsReco from "./VendorTransferToOtherAccountsReco";
import VendorTransferToOtherAccountsSplitReco, {
  TransferSplitItem,
} from "./VendorTransferToOtherAccountsSplitReco";
import VendorOwnerDrawingReco from "./VendorOwnerDrawingReco";
import VendorCustomerPaymentRefundReco from "./VendorCustomerPaymentRefundReco";
import VendorCardPaymentReco from "./VendorCardPaymentReco";
import {
  useToast,
  ErrorBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
  BankGlAccountDropdown,
  SplitTransactionAmountReviewView,
  isSplitTransactionAmountReview,
} from "../shared/presentation/components";
import {
  useErrorHandling,
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../shared/presentation/hooks";
import { dateToMonthYear } from "../shared/utils/date";
import {
  calculateSingleForeignPaymentForex,
  roundTo2Decimals,
} from "../utils/foreignPaymentForex";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../shared/utils/reviewWorkflow";
import { getStoredAccountingBackend } from "../config/accounting";

// Permissions
import { usePermissions } from "../shared/presentation/hooks/usePermissions";
import { useBankTransactionPermissions } from "../shared/presentation/hooks/useBankTransactionPermissions";
import { useBankTransactionReviewData } from "../shared/presentation/hooks/useBankTransactionReviewData";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { ReviewRequestModal } from "../shared/presentation/components/ReviewRequestModal";

interface VendorPaymentTransactionModalProps {
  open: boolean;
  zoho_bankaccountid: string | null;
  transactionData: Transaction | null;
  onOpenChange: (open: boolean) => void;
  onTransactionMatched?: (transactionId: string, amount: number) => void;
  onReviewRequestSubmitted?: () => void;
  allBankAccounts?: BankAccount[];
  currentBankAccountId?: string;
  currentBankAccountName?: string;
}

const VendorPaymentTransactionModal: React.FC<
  VendorPaymentTransactionModalProps
> = ({
  open,
  onOpenChange,
  transactionData,
  zoho_bankaccountid,
  onTransactionMatched,
  onReviewRequestSubmitted,
  allBankAccounts = [],
  currentBankAccountId = "",
  currentBankAccountName = "",
}) => {
  // Toast and error handling
  const { showSuccess, showError, showWarning } = useToast();
  const {
    errors,
    setApiErrors,
    setFieldError,
    clearErrors,
    hasErrors: checkHasErrors,
  } = useErrorHandling();

  // Permissions
  const { getPermissionInfo, hasPermission } = usePermissions();

  // Bank transaction permissions hook
  const {
    canAdvance: canAdvanceFromHook,
    canMatch,
    canSplitTransaction,
    canProcessHighValueExpense,
    needsReviewForAdvance,
    needsReviewForMatch,
    needsReviewForSplitTransaction,
    needsReviewForHighValueExpense,
    isInitialLoading,
  } = useBankTransactionPermissions();

  // Bank transaction review data hook
  const {
    reviewStatus,
    hasReview,
    reviewForPopulation,
    isLoading: isReviewDataLoading,
  } = useBankTransactionReviewData(transactionData);
  const isKnownUnderReview =
    transactionData?.review_status === "pending" || reviewStatus === "pending";
  const isWaitingForReviewData = isKnownUnderReview && !reviewForPopulation;

  const canSplit = hasPermission(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.SPLIT_TRANSACTION,
  );
  const advanceInfo = getPermissionInfo(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.ADVANCE,
  );
  const canAdvance = canAdvanceFromHook || advanceInfo.granted;

  // Closed month state
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingMatchAction, setPendingMatchAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
    actionCode?: string;
  } | null>(null);

  // Closed month check (transaction date)
  const transactionMonth = transactionData?.date
    ? dateToMonthYear(transactionData.date)
    : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(transactionMonth);
  const isAdminOrCFO = useIsAdminOrCFO();

  // Helper function to generate unique IDs
  const generateId = () => {
    return `vendor-payment-instance-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  };

  // State for multiple vendor payment instances
  interface VendorPaymentInstance {
    id: string;
    vendorId: string | null;
    vendorName: string | null;
    selectedBills: Array<{
      bill: VendorBill;
      amount: number;
      adjustmentAmount?: number;
      isSelected: boolean;
    }>;
    advancePayment: number;
    onAccountAmount: number;
    totalBillAmounts: number;
    bankExchangeRate: number | null;
  }

  const [vendorPaymentInstances, setVendorPaymentInstances] = useState<
    VendorPaymentInstance[]
  >([
    {
      id: generateId(),
      vendorId: null,
      vendorName: null,
      selectedBills: [],
      advancePayment: 0,
      onAccountAmount: 0,
      totalBillAmounts: 0,
      bankExchangeRate: null,
    },
  ]);

  // Refs for scrolling to instances
  const instanceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const newlyAddedInstanceIdRef = useRef<string | null>(null);

  // State for matching/categorization
  const [isMatching, setIsMatching] = useState(false);
  const [knockOffOpeningBalance, setKnockOffOpeningBalance] = useState(false);

  // State for transfer form data
  const [transferToAccountId, setTransferToAccountId] = useState<string>("");
  const [
    transferToAccountZohoBankAccountId,
    setTransferToAccountZohoBankAccountId,
  ] = useState<string>("");
  const [transferDate, setTransferDate] = useState<string>("");
  const [transferReferenceNumber, setTransferReferenceNumber] =
    useState<string>("");
  const [transferTransactionExists, setTransferTransactionExists] =
    useState<boolean>(false);
  const [transferToTransactionId, setTransferToTransactionId] =
    useState<string>("");

  // State for owner drawing form data
  const [ownerDrawingToAccount, setOwnerDrawingToAccount] =
    useState<string>("");
  const [ownerDrawingDate, setOwnerDrawingDate] = useState<string>("");
  const [ownerDrawingPaymentMode, setOwnerDrawingPaymentMode] =
    useState<string>("Bank Transfer");
  const [ownerDrawingReferenceNumber, setOwnerDrawingReferenceNumber] =
    useState<string>("");

  // State for customer payment refund form data
  const [customerRefundCustomer, setCustomerRefundCustomer] = useState<{
    customer_id: string;
    customer_name: string;
  }>({ customer_id: "", customer_name: "" });
  const [customerRefundDate, setCustomerRefundDate] = useState<string>("");
  const [customerRefundReferenceNumber, setCustomerRefundReferenceNumber] =
    useState<string>("");
  const [customerRefundPaymentMode, setCustomerRefundPaymentMode] =
    useState<string>("Bank Transfer");
  const [customerRefundSelectedPayment, setCustomerRefundSelectedPayment] =
    useState<{
      payment_id: string;
      payment_number: string;
      amount: number;
    } | null>(null);

  // State for card payment form data
  const [cardPaymentToAccountId, setCardPaymentToAccountId] =
    useState<string>("");
  const [cardPaymentDate, setCardPaymentDate] = useState<string>("");
  const [cardPaymentReferenceNumber, setCardPaymentReferenceNumber] =
    useState<string>("");
  const [creditCardAccounts, setCreditCardAccounts] = useState<
    CreditCardAccount[]
  >([]);

  // State for activity logs
  const [activityLogs, setActivityLogs] = useState<TransactionActivityLog[]>(
    [],
  );
  const [isLoadingActivityLogs, setIsLoadingActivityLogs] = useState(false);

  // State for expense GL selection (dropdown uses fetchExpenseGlsPaginated)
  const [selectedExpenseGlAccountId, setSelectedExpenseGlAccountId] =
    useState<string>("");
  const [selectedExpenseGlName, setSelectedExpenseGlName] =
    useState<string>("");

  // State for transfer to other accounts form data
  const [
    transferToOtherAccountsToAccountId,
    setTransferToOtherAccountsToAccountId,
  ] = useState<string>("");
  const [transferToOtherAccountsDate, setTransferToOtherAccountsDate] =
    useState<string>("");
  const [
    transferToOtherAccountsPaymentMode,
    setTransferToOtherAccountsPaymentMode,
  ] = useState<string>("Bank Transfer");
  const [
    transferToOtherAccountsReferenceNumber,
    setTransferToOtherAccountsReferenceNumber,
  ] = useState<string>("");

  // State for transfer to other accounts split
  const [isTransferSplitMode, setIsTransferSplitMode] =
    useState<boolean>(false);
  const [transferSplits, setTransferSplits] = useState<TransferSplitItem[]>([]);

  // State for form data
  const [selectedCategory, setSelectedCategory] =
    useState<string>("Vendor Payment");
  const [formChanges, setFormChanges] = useState<{
    [key: string]: any;
  }>({});
  const transactionId = transactionData?.transaction_id || "";
  const categoryPrefilledRef = useRef(false);
  const expenseGlAutoPopulatedRef = useRef(false);
  const reviewPopulatedRef = useRef(false); // Track if we've populated from review
  const [isPopulatingFromReview, setIsPopulatingFromReview] = useState(false);

  // Suggestion UX: track when values are from API recommendation; badge shows until user changes
  const [categoryFromSuggestion, setCategoryFromSuggestion] = useState(false);
  const [categorySuggestionApplied, setCategorySuggestionApplied] =
    useState(false);
  const [initialPartyLoaded, setInitialPartyLoaded] = useState(false);
  const hasSuggestedCategory = !!(
    transactionData?.zoho_category || transactionData?.category_type
  );
  const hasSuggestedParty = !!(
    transactionData?.zoho_partyid &&
    transactionData?.zoho_partyid !== "create_new"
  );
  const suggestedCategoryValue =
    transactionData?.zoho_category || transactionData?.category_type;
  const suggestionApplying =
    (hasSuggestedCategory || hasSuggestedParty) &&
    ((hasSuggestedCategory && !categorySuggestionApplied) ||
      (selectedCategory === "Vendor Payment" &&
        hasSuggestedParty &&
        !initialPartyLoaded));

  const loadActivityLogs = useCallback(async () => {
    if (!transactionId) return;
    try {
      setIsLoadingActivityLogs(true);
      const response = await fetchTransactionActivityLogs(transactionId);
      const logs = Array.isArray(response.data?.logs)
        ? response.data.logs
        : Array.isArray(response.data)
          ? response.data
          : [];
      setActivityLogs(logs);
    } catch (error) {
      console.error("Failed to load activity logs:", error);
    } finally {
      setIsLoadingActivityLogs(false);
    }
  }, [transactionId]);

  // Fetch credit card accounts when modal opens
  useEffect(() => {
    if (open) {
      fetchCreditCards();
      loadActivityLogs();
      categoryPrefilledRef.current = false;
      reviewPopulatedRef.current = false; // Reset on open
      setCategorySuggestionApplied(false);
      setCategoryFromSuggestion(false);
      setInitialPartyLoaded(false);
    } else {
      setActivityLogs([]);
      categoryPrefilledRef.current = false;
      reviewPopulatedRef.current = false; // Reset on close
      setCategorySuggestionApplied(false);
      setCategoryFromSuggestion(false);
      setInitialPartyLoaded(false);
    }
  }, [open, loadActivityLogs]);

  // Auto-populate expense GL based on zoho_gl (fetch by ID for trigger label)
  useEffect(() => {
    if (
      !open ||
      selectedCategory !== "Expense" ||
      !transactionData?.zoho_gl ||
      !shouldUseZohoGl("Expense") ||
      expenseGlAutoPopulatedRef.current
    )
      return;

    setSelectedExpenseGlAccountId(transactionData.zoho_gl);
    expenseGlAutoPopulatedRef.current = true;

    journalsAPI
      .getGLMapByAccountId(transactionData.zoho_gl)
      .then((response) => {
        if (response.success && response.data) {
          setSelectedExpenseGlName(response.data.gl_name ?? "");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedCategory, transactionData?.zoho_gl]);

  // Auto-select category based on zoho_category (priority) or backend category_type (fallback)
  useEffect(() => {
    if (!open || categoryPrefilledRef.current) return;
    const isTallyBackend = getStoredAccountingBackend() === "tally";
    const categoryTypeMap: Record<string, string> = {
      vendor_payment: "Vendor Payment",
      transfer_to_another_account: "Transfer To Another Account",
      transfer_to_other_accounts: "Transfer to Other Accounts",
      transfer_to_other_accounts_split: "Transfer to Other Accounts",
      ...(isTallyBackend
        ? {}
        : {
            owner_drawing: "Owner Drawing",
            card_payment: "Card Payment",
          }),
      customer_payment_refund: "Customer Payment Refund",
      vendor_expense: "Expense",
    };

    // Priority: Use zoho_category if available, otherwise fallback to category_type
    const categoryValue =
      transactionData?.zoho_category || transactionData?.category_type;
    const mappedCategory = categoryValue && categoryTypeMap[categoryValue];

    if (mappedCategory) {
      setSelectedCategory(mappedCategory);
      categoryPrefilledRef.current = true;
      setCategorySuggestionApplied(true);
      setCategoryFromSuggestion(true);
    } else if (categoryValue) {
      // API sent a category we don't map - stop waiting, unblock UI
      setCategorySuggestionApplied(true);
    }
  }, [open, transactionData?.zoho_category, transactionData?.category_type]);

  // Set category from review raw_json when review exists (view-only mode)
  useEffect(() => {
    if (
      !open ||
      !reviewForPopulation ||
      reviewPopulatedRef.current ||
      reviewStatus === "rejected"
    ) {
      setIsPopulatingFromReview(false);
      return;
    }

    // Only set category if review is pending or approved (for view-only mode)
    if (reviewStatus !== "pending") {
      setIsPopulatingFromReview(false);
      return;
    }

    const rawJson = reviewForPopulation.change_details?.raw_json;
    if (!rawJson) {
      setIsPopulatingFromReview(false);
      return;
    }

    setIsPopulatingFromReview(false); // No loading needed for view-only mode

    // Map category_type to selectedCategory
    const categoryTypeMap: Record<string, string> = {
      vendor_payment: "Vendor Payment",
      split_vendor: "Vendor Payment",
      transfer_to_another_account: "Transfer To Another Account",
      transfer_to_other_accounts: "Transfer to Other Accounts",
      transfer_to_other_accounts_split: "Transfer to Other Accounts",
      owner_drawing: "Owner Drawing",
      customer_payment_refund: "Customer Payment Refund",
      card_payment: "Card Payment",
      vendor_expense: "Expense",
      expense: "Expense",
    };

    const categoryType = rawJson.category_type;
    if (categoryType && categoryTypeMap[categoryType]) {
      setSelectedCategory(categoryTypeMap[categoryType]);
      categoryPrefilledRef.current = true;
    }

    reviewPopulatedRef.current = true;
  }, [open, reviewForPopulation, reviewStatus]);

  const fetchCreditCards = async () => {
    try {
      const response = await fetchCreditCardAccounts();
      if (response.success) {
        setCreditCardAccounts(response.data.credit_card_accounts);
      }
    } catch (error) {
      console.error("Failed to fetch credit card accounts:", error);
    }
  };

  // Initialize first instance with auto-select vendor if provided
  useEffect(() => {
    if (
      open &&
      selectedCategory === "Vendor Payment" &&
      transactionData?.zoho_partyid &&
      transactionData?.zoho_partyid !== "create_new" &&
      vendorPaymentInstances.length === 1 &&
      !vendorPaymentInstances[0].vendorId
    ) {
      // Auto-select will be handled by VendorPaymentReco component
      // Just pass the auto-select props
    }
  }, [
    open,
    selectedCategory,
    transactionData?.zoho_partyid,
    vendorPaymentInstances,
  ]);

  // Auto-scroll to newly added instance
  useEffect(() => {
    if (newlyAddedInstanceIdRef.current) {
      const instanceId = newlyAddedInstanceIdRef.current;
      const instanceRef = instanceRefs.current.get(instanceId);
      if (instanceRef) {
        setTimeout(() => {
          instanceRef.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
      }
      newlyAddedInstanceIdRef.current = null;
    }
  }, [vendorPaymentInstances]);

  // Calculate total allocated amounts from other instances (excluding specified instance)
  const calculateTotalAllocatedAmountsFromOtherInstances = useCallback(
    (excludeInstanceId: string): number => {
      return vendorPaymentInstances
        .filter((instance) => instance.id !== excludeInstanceId)
        .reduce((sum, instance) => sum + instance.totalBillAmounts, 0);
    },
    [vendorPaymentInstances],
  );

  // Calculate total advance payments across all instances
  const calculateTotalAdvancePayments = useCallback((): number => {
    return vendorPaymentInstances.reduce(
      (sum, instance) => sum + instance.advancePayment,
      0,
    );
  }, [vendorPaymentInstances]);

  const calculateTotalOnAccountAmounts = useCallback((): number => {
    return vendorPaymentInstances.reduce(
      (sum, instance) => sum + instance.onAccountAmount,
      0,
    );
  }, [vendorPaymentInstances]);

  // Calculate total bill amounts across all instances
  const calculateTotalBillAmounts = useCallback((): number => {
    return vendorPaymentInstances.reduce(
      (sum, instance) => sum + instance.totalBillAmounts,
      0,
    );
  }, [vendorPaymentInstances]);

  // Validate match requirements for multiple instances
  const validateMatchRequirements = useCallback((): boolean => {
    if (vendorPaymentInstances.length === 1) {
      return true; // No validation needed for single instance
    }
    const totalAmounts = calculateTotalBillAmounts();
    const totalAdvance = calculateTotalAdvancePayments();
    const totalOnAccount = calculateTotalOnAccountAmounts();
    const total = totalAmounts + totalAdvance + totalOnAccount;
    const transactionAmountValue = transactionData?.amount || 0;

    // Round both values to 2 decimal places to normalize floating-point precision errors
    // Then compare exactly for strict cent-level matching
    // This is the standard approach for currency validation in production
    const roundedTotal = Math.round(total * 100) / 100;
    const roundedTransactionAmount =
      Math.round(transactionAmountValue * 100) / 100;

    return roundedTotal === roundedTransactionAmount;
  }, [
    vendorPaymentInstances,
    calculateTotalBillAmounts,
    calculateTotalAdvancePayments,
    calculateTotalOnAccountAmounts,
    transactionData?.amount,
  ]);

  // Handle adding a new vendor instance
  const handleAddVendor = useCallback(() => {
    // Check split_transaction permission
    if (!canSplit) {
      showError("You do not have permission to split transactions");
      return;
    }
    const newInstance: VendorPaymentInstance = {
      id: generateId(),
      vendorId: null,
      vendorName: null,
      selectedBills: [],
      advancePayment: 0,
      onAccountAmount: 0,
      totalBillAmounts: 0,
      bankExchangeRate: null,
    };
    setVendorPaymentInstances((prev) => {
      const newInstances = [...prev, newInstance];
      // If transitioning from 1 to multiple instances, clear advance payments from all instances
      if (prev.length === 1) {
        return newInstances.map((instance) => ({
          ...instance,
          advancePayment: 0,
          onAccountAmount: 0,
        }));
      }
      return newInstances;
    });
    newlyAddedInstanceIdRef.current = newInstance.id;
  }, [canSplit, showError]);

  // Handle removing a vendor instance
  const handleRemoveVendor = useCallback(
    (instanceId: string) => {
      if (vendorPaymentInstances.length <= 1) {
        return; // Don't allow removing the last instance
      }
      setVendorPaymentInstances((prev) =>
        prev.filter((instance) => instance.id !== instanceId),
      );
      instanceRefs.current.delete(instanceId);
      instanceChangeCallbacksRef.current.delete(instanceId);
    },
    [vendorPaymentInstances.length],
  );

  // Handle instance change from VendorPaymentReco
  const handleInstanceChange = useCallback(
    (
      instanceId: string,
      data: {
        vendorId: string | null;
        vendorName: string | null;
        selectedBills: Array<{
          bill: VendorBill;
          amount: number;
          adjustmentAmount?: number;
          isSelected: boolean;
        }>;
        advancePayment: number;
        onAccountAmount: number;
        totalBillAmounts: number;
        bankExchangeRate: number | null;
      },
    ) => {
      setVendorPaymentInstances((prev) =>
        prev.map((instance) =>
          instance.id === instanceId
            ? {
                ...instance,
                vendorId: data.vendorId,
                vendorName: data.vendorName,
                selectedBills: data.selectedBills,
                advancePayment: data.advancePayment,
                onAccountAmount: data.onAccountAmount,
                totalBillAmounts: data.totalBillAmounts,
                bankExchangeRate: data.bankExchangeRate,
              }
            : instance,
        ),
      );
    },
    [],
  );

  // Ref callback for instance refs
  const setInstanceRef = useCallback((instanceId: string) => {
    return (el: HTMLDivElement | null) => {
      if (el) {
        instanceRefs.current.set(instanceId, el);
      } else {
        instanceRefs.current.delete(instanceId);
      }
    };
  }, []);

  // Memoized onChange callbacks per instance to avoid infinite loops
  const instanceChangeCallbacksRef = useRef<
    Map<
      string,
      (data: {
        vendorId: string | null;
        vendorName: string | null;
        selectedBills: Array<{
          bill: VendorBill;
          amount: number;
          adjustmentAmount?: number;
          isSelected: boolean;
        }>;
        advancePayment: number;
        onAccountAmount: number;
        totalBillAmounts: number;
        bankExchangeRate: number | null;
      }) => void
    >
  >(new Map());

  // Store handleInstanceChange in a ref to avoid recreating callbacks
  const handleInstanceChangeRef = useRef(handleInstanceChange);
  useEffect(() => {
    handleInstanceChangeRef.current = handleInstanceChange;
  }, [handleInstanceChange]);

  const getInstanceChangeCallback = useCallback((instanceId: string) => {
    if (!instanceChangeCallbacksRef.current.has(instanceId)) {
      instanceChangeCallbacksRef.current.set(instanceId, (data) => {
        handleInstanceChangeRef.current(instanceId, data);
      });
    }
    return instanceChangeCallbacksRef.current.get(instanceId)!;
  }, []);

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  // Helper function to format numbers with 2 decimal places (Indian format)
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Helper function to extract meaningful error message
  const extractErrorMessage = (error: any): string => {
    if (!error) return "An unknown error occurred";

    console.log("Full error object:", error);

    // Check for validation errors with errors object
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors as Record<string, unknown>;
      const errorMessages = Object.keys(errors)
        .map((field) => `${field}: ${errors[field]}`)
        .join("\n");

      const validationMessage =
        error.response?.data?.message || "Validation failed";
      return `${validationMessage}\n\n${errorMessages}`;
    }

    let errorMessage = "";

    // Extract error message from various error object structures
    // Check for Axios error response structure first
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.error) {
      errorMessage = error.error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    } else {
      errorMessage = "An unknown error occurred";
    }

    console.log("Original error from API:", errorMessage);

    // Check for Zoho API errors
    if (errorMessage.includes("Zoho API request failed:")) {
      const zohoErrorMatch = errorMessage.match(
        /Zoho API request failed:\s*(.+)/,
      );
      if (zohoErrorMatch && zohoErrorMatch[1]) {
        const cleanMessage = zohoErrorMatch[1].trim();
        console.log("Extracted Zoho error:", cleanMessage);
        return cleanMessage;
      }
    }

    // For simple messages, check if there's nested "Error: " pattern
    const lastErrorIndex = errorMessage.lastIndexOf("Error: ");
    if (lastErrorIndex !== -1) {
      const extractedMessage = errorMessage
        .substring(lastErrorIndex + 7) // "Error: " is 7 characters
        .trim();
      if (extractedMessage) {
        console.log("Extracted clean message:", extractedMessage);
        return extractedMessage;
      }
    }

    // Return the message as is
    console.log("Returning message as is:", errorMessage);
    return errorMessage;
  };

  const handleClose = () => {
    // Reset form state when closing
    setSelectedCategory("Vendor Payment");
    setVendorPaymentInstances([
      {
        id: generateId(),
        vendorId: null,
        vendorName: null,
        selectedBills: [],
        advancePayment: 0,
        onAccountAmount: 0,
        totalBillAmounts: 0,
        bankExchangeRate: null,
      },
    ]);
    setFormChanges({});
    setKnockOffOpeningBalance(false);
    instanceRefs.current.clear();
    newlyAddedInstanceIdRef.current = null;
    // Reset transfer form
    setTransferToAccountId("");
    setTransferToAccountZohoBankAccountId("");
    setTransferDate("");
    setTransferReferenceNumber("");
    setTransferTransactionExists(false);
    setTransferToTransactionId("");
    // Reset owner drawing form
    setOwnerDrawingToAccount("");
    setOwnerDrawingDate("");
    setOwnerDrawingPaymentMode("Bank Transfer");
    setOwnerDrawingReferenceNumber("");
    // Reset customer payment refund form
    setCustomerRefundCustomer({ customer_id: "", customer_name: "" });
    setCustomerRefundDate("");
    setCustomerRefundReferenceNumber("");
    setCustomerRefundPaymentMode("Bank Transfer");
    setCustomerRefundSelectedPayment(null);
    // Reset card payment form
    setCardPaymentToAccountId("");
    setCardPaymentDate("");
    setCardPaymentReferenceNumber("");
    // Reset expense form
    setSelectedExpenseGlAccountId("");
    setSelectedExpenseGlName("");
    // Reset transfer to other accounts form
    setTransferToOtherAccountsToAccountId("");
    setTransferToOtherAccountsDate("");
    setTransferToOtherAccountsPaymentMode("Bank Transfer");
    setTransferToOtherAccountsReferenceNumber("");
    setIsTransferSplitMode(false);
    setTransferSplits([]);
    clearErrors();
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form state when closing by any means (including clicking outside)
      setSelectedCategory("Vendor Payment");
      setVendorPaymentInstances([
        {
          id: generateId(),
          vendorId: null,
          vendorName: null,
          selectedBills: [],
          advancePayment: 0,
          onAccountAmount: 0,
          totalBillAmounts: 0,
          bankExchangeRate: null,
        },
      ]);
      setFormChanges({});
    setKnockOffOpeningBalance(false);
      instanceRefs.current.clear();
      newlyAddedInstanceIdRef.current = null;
      // Reset transfer form
      setTransferToAccountId("");
      setTransferToAccountZohoBankAccountId("");
      setTransferDate("");
      setTransferReferenceNumber("");
      setTransferTransactionExists(false);
      setTransferToTransactionId("");
      // Reset owner drawing form
      setOwnerDrawingToAccount("");
      setOwnerDrawingDate("");
      setOwnerDrawingPaymentMode("Bank Transfer");
      setOwnerDrawingReferenceNumber("");
      // Reset customer payment refund form
      setCustomerRefundCustomer({ customer_id: "", customer_name: "" });
      setCustomerRefundDate("");
      setCustomerRefundReferenceNumber("");
      setCustomerRefundPaymentMode("Bank Transfer");
      setCustomerRefundSelectedPayment(null);
      // Reset card payment form
      setCardPaymentToAccountId("");
      setCardPaymentDate("");
      setCardPaymentReferenceNumber("");
      // Reset expense form
      setSelectedExpenseGlAccountId("");
      setSelectedExpenseGlName("");
      // Reset transfer to other accounts form
      setTransferToOtherAccountsToAccountId("");
      setTransferToOtherAccountsDate("");
      setTransferToOtherAccountsPaymentMode("Bank Transfer");
      setTransferToOtherAccountsReferenceNumber("");
      setIsTransferSplitMode(false);
      setTransferSplits([]);
      // Clear errors
      clearErrors();
    }
    onOpenChange(newOpen);
  };

  // Helper function to build request body for a category
  // Helper function to build empty old data for comparison
  const buildEmptyOldDataForCategory = (category: string): any => {
    switch (category) {
      case "Vendor Payment":
        return {
          advance_payment: 0,
          categorize_in_zoho_json: {
            bills: [],
            exchange_rate: null,
            bank_charges: null,
          },
        };
      case "Transfer To Another Account":
        return {
          to_account_id: "",
          date: "",
          reference_number: "",
        };
      case "Transfer to Other Accounts":
        return {
          to_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
        };
      case "Owner Drawing":
        return {
          to_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
        };
      case "Customer Payment Refund":
        return {
          customer_id: "",
          customer_name: "",
          date: "",
          payment_mode: "",
          reference_number: "",
          payment: null,
        };
      case "Card Payment":
        return {
          to_account_id: "",
          date: "",
          reference_number: "",
        };
      case "Expense":
        return {
          categorize_in_zoho_json: {
            account_id: "",
          },
        };
      default:
        return {};
    }
  };

  const buildRequestBodyForCategory = (category: string): any => {
    if (!transactionData || !zoho_bankaccountid) {
      return {};
    }

    switch (category) {
      case "Vendor Payment":
        if (vendorPaymentInstances.length === 1) {
          // Single vendor payment
          const instance = vendorPaymentInstances[0];
          const selectedBillsWithAmounts = instance.selectedBills.filter(
            (item) => item.isSelected && item.amount > 0,
          );

          const hasForeign = instance.selectedBills.some(
            (item) =>
              item.bill.currency_code && item.bill.currency_code !== "INR",
          );

          const isForeign =
            hasForeign &&
            instance.bankExchangeRate &&
            instance.bankExchangeRate > 0;

          const appliedAmountFC = selectedBillsWithAmounts.reduce(
            (sum, item) => sum + item.amount,
            0,
          );
          const foreignForex = isForeign
            ? calculateSingleForeignPaymentForex(
                appliedAmountFC,
                instance.bankExchangeRate!,
                transactionData.amount,
              )
            : null;
          const amountForRequest = foreignForex
            ? foreignForex.amountForZoho
            : roundTo2Decimals(transactionData.amount);

          const requestBody: any = {
            category_type: "vendor_payment",
            transaction_id: transactionData.transaction_id || "",
            vendor_name: instance.vendorName,
            advance_payment: instance.advancePayment,
            on_account_amount: instance.onAccountAmount,
            categorize_in_zoho_json: {
              vendor_id: instance.vendorId,
              date: transactionData.date,
              bills: selectedBillsWithAmounts.map((item) => ({
                bill_id: item.bill.bill_id,
                bill_number: item.bill.bill_number || item.bill.bill_id,
                amount_applied: roundTo2Decimals(item.amount),
                adjustment_amount: item.adjustmentAmount || 0,
                total: item.bill.total,
                balance: item.bill.balance,
                status: item.bill.status,
                currency_code: item.bill.currency_code,
              })),
              amount: amountForRequest,
              paid_through_account_id: zoho_bankaccountid,
            },
          };

          if (isForeign && foreignForex) {
            requestBody.categorize_in_zoho_json.exchange_rate =
              foreignForex.exchangeRateForZoho;
            requestBody.categorize_in_zoho_json.bank_charges =
              foreignForex.bankChargesFC;
          }

          return requestBody;
        } else {
          // Multiple vendors
          const vendors = vendorPaymentInstances.map((instance) => {
            const selectedBillsWithAmounts = instance.selectedBills.filter(
              (item) => item.isSelected && item.amount > 0,
            );

            const hasForeign = instance.selectedBills.some(
              (item) =>
                item.isSelected &&
                item.bill.currency_code &&
                item.bill.currency_code !== "INR",
            );

            let exchangeRate: number | null = null;
            let bankCharges: number | null = null;

            if (
              hasForeign &&
              instance.bankExchangeRate &&
              instance.bankExchangeRate > 0
            ) {
              exchangeRate =
                Math.trunc(instance.bankExchangeRate * 1000000) / 1000000;

              const realisationINR = selectedBillsWithAmounts.reduce(
                (sum, item) => sum + item.amount * instance.bankExchangeRate!,
                0,
              );

              const vendorAllocatedINR = selectedBillsWithAmounts.reduce(
                (sum, item) => {
                  const billAmountINR =
                    item.amount * instance.bankExchangeRate!;
                  return sum + billAmountINR;
                },
                instance.advancePayment + instance.onAccountAmount,
              );

              const calculatedBankCharges = Math.abs(
                realisationINR - vendorAllocatedINR,
              );

              if (calculatedBankCharges > 0.01) {
                bankCharges =
                  Math.trunc(
                    (calculatedBankCharges / instance.bankExchangeRate!) * 100,
                  ) / 100;
              }
            }

            return {
              vendor_id: instance.vendorId!,
              vendor_name: instance.vendorName!,
              advance_payment: instance.advancePayment,
              on_account_amount: instance.onAccountAmount,
              exchange_rate: exchangeRate,
              bank_charges: bankCharges,
              bills: selectedBillsWithAmounts.map((item) => ({
                bill_id: item.bill.bill_id,
                bill_number: item.bill.bill_number || item.bill.bill_id,
                amount_applied: roundTo2Decimals(item.amount),
                adjustment_amount: item.adjustmentAmount || 0,
                total: item.bill.total,
                balance: item.bill.balance,
                currency_code: item.bill.currency_code,
                currency_symbol: item.bill.currency_symbol,
                status: item.bill.status,
              })),
            };
          });

          return {
            transaction_id: transactionData.transaction_id || "",
            vendors: vendors,
            date: transactionData.date,
            account_id: zoho_bankaccountid!,
            category_type: "split_vendor",
          };
        }

      case "Transfer To Another Account":
        return {
          transaction_id: transactionData.transaction_id || "",
          to_transaction_id: transferToTransactionId,
          date: transferDate,
          from_account_id: zoho_bankaccountid,
          to_account_id: transferToAccountZohoBankAccountId,
          amount: transactionData.amount,
          category_type: "transfer_to_another_account",
          reference_number: transferReferenceNumber || "",
        };

      case "Transfer to Other Accounts":
        if (isTransferSplitMode) {
          return {
            transaction_id: transactionData.transaction_id || "",
            from_account_id: zoho_bankaccountid,
            date: transferToOtherAccountsDate,
            reference_number: transferToOtherAccountsReferenceNumber || "",
            splits: transferSplits.map((split) => ({
              to_account_id: split.to_account_id,
              amount: split.amount,
              payment_mode: split.payment_mode,
            })),
          };
        }
        return {
          category_type: "transfer_to_other_accounts",
          transaction_id: transactionData.transaction_id || "",
          from_account_id: zoho_bankaccountid,
          to_account_id: transferToOtherAccountsToAccountId,
          amount: transactionData.amount,
          date: transferToOtherAccountsDate,
          payment_mode: transferToOtherAccountsPaymentMode,
          reference_number: transferToOtherAccountsReferenceNumber || "",
        };

      case "Owner Drawing":
        return {
          category_type: "owner_drawing",
          transaction_id: transactionData.transaction_id || "",
          from_account_id: zoho_bankaccountid,
          to_account_id: ownerDrawingToAccount,
          amount: transactionData.amount,
          date: ownerDrawingDate,
          payment_mode: ownerDrawingPaymentMode,
          reference_number: ownerDrawingReferenceNumber || undefined,
        };

      case "Customer Payment Refund": {
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        const refundAmount = isTallyBackend
          ? transactionData.amount
          : customerRefundSelectedPayment?.amount;
        return {
          category_type: "customer_payment_refund",
          transaction_id: transactionData.transaction_id || "",
          customer_id: customerRefundCustomer.customer_id,
          customer_name: customerRefundCustomer.customer_name,
          payment: isTallyBackend
            ? {
                payment_id: customerRefundSelectedPayment?.payment_id || "",
                payment_number:
                  customerRefundSelectedPayment?.payment_number || "",
                amount: refundAmount,
              }
            : {
                payment_id: customerRefundSelectedPayment?.payment_id,
                payment_number: customerRefundSelectedPayment?.payment_number,
                amount: customerRefundSelectedPayment?.amount,
              },
          date: customerRefundDate,
          payment_mode: customerRefundPaymentMode,
          reference_number: customerRefundReferenceNumber || "",
          from_account_id: zoho_bankaccountid,
          amount: refundAmount,
        };
      }

      case "Card Payment":
        return {
          category_type: "card_payment",
          transaction_id: transactionData.transaction_id || "",
          from_account_id: zoho_bankaccountid,
          to_account_id: cardPaymentToAccountId,
          amount: transactionData.amount,
          date: cardPaymentDate,
          reference_number: cardPaymentReferenceNumber || "",
        };

      case "Expense":
        return {
          category_type: "expense",
          transaction_id: transactionData.transaction_id || "",
          description: transactionData.description || "",
          transaction_date: transactionData.date,
          transaction_amount: transactionData.amount,
          currency_symbol: transactionData.currency_symbol || "₹",
          expense_account_name: selectedExpenseGlName || "",
          categorize_in_zoho_json: {
            account_id: selectedExpenseGlAccountId,
            account_name: selectedExpenseGlName || "",
            paid_through_account_id: zoho_bankaccountid,
            date: transactionData.date,
            amount: transactionData.amount,
          },
        };

      default:
        return {};
    }
  };

  // Helper function to build field changes for review
  const buildFieldChangesForCategory = (
    category: string,
    oldData: any,
    newData: any,
  ): Array<{ field: string; old: string; new: string }> => {
    const changes: Array<{ field: string; old: string; new: string }> = [];

    switch (category) {
      case "Vendor Payment":
        if (oldData?.vendor_name !== newData?.vendor_name) {
          changes.push({
            field: "Vendor",
            old: oldData?.vendor_name || "N/A",
            new: newData?.vendor_name || "N/A",
          });
        }
        if (oldData?.advance_payment !== newData?.advance_payment) {
          changes.push({
            field: "Advance Payment",
            old: (oldData?.advance_payment || 0).toString(),
            new: (newData?.advance_payment || 0).toString(),
          });
        }
        newData?.categorize_in_zoho_json?.bills?.forEach(
          (bill: any, index: number) => {
            const oldBill = oldData?.categorize_in_zoho_json?.bills?.[index];
            if (
              oldBill &&
              oldBill.adjustment_amount !== bill.adjustment_amount
            ) {
              changes.push({
                field: `Bill ${bill.bill_number}: Adjustment`,
                old: (oldBill.adjustment_amount || 0).toString(),
                new: (bill.adjustment_amount || 0).toString(),
              });
            }
          },
        );
        break;

      case "Transfer To Another Account":
        if (oldData?.to_account_id !== newData?.to_account_id) {
          changes.push({
            field: "To Account",
            old: oldData?.to_account_id || "N/A",
            new: newData?.to_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Transfer Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        break;

      case "Owner Drawing":
        if (oldData?.to_account_id !== newData?.to_account_id) {
          changes.push({
            field: "To Account",
            old: oldData?.to_account_id || "N/A",
            new: newData?.to_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        break;

      case "Customer Payment Refund":
        if (oldData?.customer_name !== newData?.customer_name) {
          changes.push({
            field: "Customer",
            old: oldData?.customer_name || "N/A",
            new: newData?.customer_name || "N/A",
          });
        }
        if (oldData?.payment?.payment_id !== newData?.payment?.payment_id) {
          changes.push({
            field: "Payment",
            old: oldData?.payment?.payment_number || "N/A",
            new: newData?.payment?.payment_number || "N/A",
          });
        }
        break;

      case "Card Payment":
        if (oldData?.to_account_id !== newData?.to_account_id) {
          changes.push({
            field: "To Account",
            old: oldData?.to_account_id || "N/A",
            new: newData?.to_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        break;

      case "Expense":
        if (
          oldData?.categorize_in_zoho_json?.account_id !==
          newData?.categorize_in_zoho_json?.account_id
        ) {
          changes.push({
            field: "Expense GL Account",
            old: oldData?.categorize_in_zoho_json?.account_id || "N/A",
            new: newData?.categorize_in_zoho_json?.account_id || "N/A",
          });
        }
        break;
    }

    return changes;
  };

  // Check if we're in view-only mode (review pending or approved)
  const isViewOnlyMode = (): boolean => {
    return isKnownUnderReview;
  };

  // Get raw_json from review
  const getReviewRawJson = (): any => {
    return reviewForPopulation?.change_details?.raw_json || null;
  };

  // View component for vendor_payment / split_vendor category
  const renderVendorPaymentView = () => {
    const rawJson = getReviewRawJson();
    if (
      !rawJson ||
      (rawJson.category_type !== "vendor_payment" &&
        rawJson.category_type !== "split_vendor")
    ) {
      return null;
    }

    // Handle multiple vendors (split transaction)
    if (rawJson.vendors && Array.isArray(rawJson.vendors)) {
      return (
        <div className="flex w-full flex-col items-start gap-4">
          {rawJson.vendors.map((vendor: any, vendorIndex: number) => {
            const bills = vendor.bills || [];
            const vendorName = vendor.vendor_name || "N/A";
            const advancePayment = vendor.advance_payment || 0;
            const onAccountAmount = vendor.on_account_amount || 0;
            const currencySymbol =
              bills[0]?.currency_symbol ||
              transactionData?.currency_symbol ||
              "₹";

            // Amount used for payments = sum of amount_applied from bills
            const amountUsedForPayments = bills.reduce(
              (sum: number, bill: any) => sum + (bill.amount_applied || 0),
              0,
            );

            return (
              <div
                key={vendorIndex}
                className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4"
              >
                <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Vendor {vendorIndex + 1}
                  </span>
                  <span className="text-body font-body text-default-font">
                    {vendorName}
                  </span>
                </div>

                {bills.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Bills ({bills.length})
                    </span>
                    <div className="flex w-full flex-col items-start gap-3">
                      {bills.map((bill: any, index: number) => (
                        <div
                          key={index}
                          className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-4 py-3"
                        >
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-default-font">
                              {bill.bill_number || `Bill ${index + 1}`}
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {currencySymbol}
                              {formatAmount(bill.amount_applied || 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary Section */}
                <div className="flex w-full flex-col items-start gap-3 border-t border-solid border-neutral-border pt-4 mt-2">
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Amount used for Payments
                    </span>
                    <span className="text-body font-body text-success-600">
                      {currencySymbol}
                      {formatAmount(amountUsedForPayments)}
                    </span>
                  </div>
                  {advancePayment > 0 && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Advance Payment
                      </span>
                      <span className="text-body font-body text-default-font">
                        {currencySymbol}
                        {formatAmount(advancePayment)}
                      </span>
                    </div>
                  )}
                  {onAccountAmount > 0 && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Opening balance
                      </span>
                      <span className="text-body font-body text-default-font">
                        {currencySymbol}
                        {formatAmount(onAccountAmount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Handle single vendor
    const categorizeData = rawJson.categorize_in_zoho_json;
    const bills = categorizeData?.bills || [];
    const vendorName = rawJson.vendor_name || "N/A";
    const advancePayment = rawJson.advance_payment || 0;
    const onAccountAmount = rawJson.on_account_amount || 0;
    const currencySymbol =
      bills[0]?.currency_symbol || transactionData?.currency_symbol || "₹";

    // Amount used for payments = sum of amount_applied from bills
    const amountUsedForPayments = bills.reduce(
      (sum: number, bill: any) => sum + (bill.amount_applied || 0),
      0,
    );

    return (
      <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body-bold font-body-bold text-default-font">
              Vendor
            </span>
            <span className="text-body font-body text-default-font">
              {vendorName}
            </span>
          </div>

          {bills.length > 0 && (
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Bills ({bills.length})
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                {bills.map((bill: any, index: number) => (
                  <div
                    key={index}
                    className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-4 py-3"
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-default-font">
                        {bill.bill_number || `Bill ${index + 1}`}
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {currencySymbol}
                        {formatAmount(bill.amount_applied || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Section */}
          <div className="flex w-full flex-col items-start gap-3 border-t border-solid border-neutral-border pt-4 mt-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Amount used for Payments
              </span>
              <span className="text-body font-body text-success-600">
                {currencySymbol}
                {formatAmount(amountUsedForPayments)}
              </span>
            </div>
            {advancePayment > 0 && (
              <div className="flex w-full items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  Advance Payment
                </span>
                <span className="text-body font-body text-default-font">
                  {currencySymbol}
                  {formatAmount(advancePayment)}
                </span>
              </div>
            )}
            {onAccountAmount > 0 && (
              <div className="flex w-full items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  Opening balance
                </span>
                <span className="text-body font-body text-default-font">
                  {currencySymbol}
                  {formatAmount(onAccountAmount)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // View component for other categories
  const renderOtherCategoryView = () => {
    const rawJson = getReviewRawJson();
    if (!rawJson) {
      return null;
    }

    const categoryType = rawJson.category_type;

    if (categoryType === "transfer_to_another_account") {
      const toAccount = allBankAccounts.find(
        (acc) => acc.zoho_bankaccountid === rawJson.to_account_id,
      );
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              To Account:
            </span>
            <span className="text-body font-body text-default-font">
              {toAccount?.bank_name || rawJson.to_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "transfer_to_other_accounts") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              To Account:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.to_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "transfer_to_other_accounts_split") {
      const splits = rawJson.splits ?? [];
      const currencySymbol =
        rawJson.currency_symbol ?? transactionData?.currency_symbol ?? "₹";
      return (
        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full flex-col gap-2">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Transfer to Other Accounts (Split)
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Split across {splits.length} destination(s)
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
          {splits.length > 0 && (
            <div className="flex w-full flex-col gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Split Payments
              </span>
              <div className="overflow-x-auto rounded border border-neutral-border bg-white">
                <table className="w-full min-w-[280px]">
                  <thead>
                    <tr className="border-b border-neutral-border bg-neutral-50">
                      <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                        To Account
                      </th>
                      <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                        Amount
                      </th>
                      <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                        Payment Mode
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {splits.map((split: any, i: number) => (
                      <tr
                        key={i}
                        className="border-b border-neutral-border last:border-b-0"
                      >
                        <td className="px-2 py-1.5 text-body text-default-font">
                          {split.to_account_name ?? split.to_account_id ?? "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                          {currencySymbol}{" "}
                          {Number(split.amount ?? 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-2 py-1.5 text-body text-subtext-color">
                          {split.payment_mode ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex w-full items-center justify-between border-t border-neutral-border pt-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Total
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {currencySymbol}{" "}
                  {Number(
                    rawJson.transaction_amount ??
                      splits.reduce(
                        (s: number, sp: any) => s + (sp.amount ?? 0),
                        0,
                      ),
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "owner_drawing") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              To Account:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.to_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "customer_payment_refund") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Customer:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.customer_name || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment?.payment_number || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "card_payment") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              To Account (Credit Card):
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.to_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "vendor_expense" || categoryType === "expense") {
      const accountName = (
        rawJson.categorize_in_zoho_json as { account_name?: string } | undefined
      )?.account_name;
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Expense GL Account:
            </span>
            <span className="text-body font-body text-default-font">
              {accountName ||
                rawJson.categorize_in_zoho_json?.account_id ||
                "N/A"}
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleMatch = async () => {
    setIsMatching(true);
    clearErrors();

    try {
      // Check if transaction data is available
      if (!transactionData || !zoho_bankaccountid) {
        showError("Missing required transaction data.");
        setFieldError("general", "Missing required transaction data");
        setIsMatching(false);
        return;
      }

      // Closed month: non-Admin/CFO must submit via review
      if (isMonthClosed === true && !isAdminOrCFO) {
        setShowClosedMonthConfirmModal(true);
        setIsMatching(false);
        return;
      }

      // Determine which actions need review (for all categories)
      const needsMatchReview = canMatch && needsReviewForMatch;

      // Category-specific review checks (only for Vendor Payment)
      let needsAdvanceReview = false;
      let needsSplitReview = false;
      let needsHighValueExpenseReview = false;
      let totalAdvance = 0;
      let hasMultipleVendors = false;

      if (selectedCategory === "Vendor Payment") {
        totalAdvance = calculateTotalAdvancePayments();
        hasMultipleVendors = vendorPaymentInstances.length > 1;
        needsAdvanceReview =
          totalAdvance > 0 && canAdvance && needsReviewForAdvance;
        needsSplitReview =
          hasMultipleVendors &&
          canSplitTransaction &&
          needsReviewForSplitTransaction;
      } else if (selectedCategory === "Expense") {
        // Check if expense amount > 10,000
        const expenseAmount = transactionData?.amount || 0;
        needsHighValueExpenseReview =
          expenseAmount > 10000 &&
          canProcessHighValueExpense &&
          needsReviewForHighValueExpense;
      }

      // Check if any action needs review (for all categories)
      if (
        needsMatchReview ||
        needsAdvanceReview ||
        needsSplitReview ||
        needsHighValueExpenseReview
      ) {
        // Check if review already exists
        if (hasReview) {
          if (reviewStatus === "pending") {
            showWarning(
              "A review request is already pending for this transaction. Please wait for it to be reviewed.",
            );
            setIsMatching(false);
            return;
          } else if (reviewStatus === "rejected") {
            showError(
              "Your review request was rejected. Please contact your manager.",
            );
            setIsMatching(false);
            return;
          }
        } else {
          // No review exists, need to create one
          // Determine primary action code
          // Priority: Split transaction review > Advance review > High-Value Expense review > Match review
          let primaryActionCode: string;
          if (needsSplitReview) {
            // Split transaction needs review - use SPLIT_TRANSACTION action
            primaryActionCode = ACTION_CODES.SPLIT_TRANSACTION;
          } else if (needsAdvanceReview) {
            // Advance payment needs review - use ADVANCE action
            primaryActionCode = ACTION_CODES.ADVANCE;
          } else if (needsHighValueExpenseReview) {
            // High-value expense needs review - use EXPENSE_HIGH_VALUE action
            primaryActionCode = ACTION_CODES.EXPENSE_HIGH_VALUE;
          } else {
            // Match needs review - use MATCH action
            primaryActionCode = ACTION_CODES.MATCH;
          }

          // Build request body (raw_json) - this matches exactly what we send to API
          const requestBody = buildRequestBodyForCategory(selectedCategory);

          // Build field changes (compare current state vs new state)
          // For now, we'll use a simplified approach - compare with empty/zero state
          const oldData = buildEmptyOldDataForCategory(selectedCategory);
          const newData = requestBody;
          const fieldChanges = buildFieldChangesForCategory(
            selectedCategory,
            oldData,
            newData,
          );

          // Build change details
          const expenseAmount = transactionData?.amount || 0;
          const changeDetails: ChangeDetails = {
            old_value: "0",
            new_value: needsHighValueExpenseReview
              ? expenseAmount.toString()
              : totalAdvance > 0
                ? totalAdvance.toString()
                : "Transaction match",
            reason: needsSplitReview
              ? "Split payment requires review"
              : needsAdvanceReview
                ? "Match transaction with advance payment requires review"
                : needsHighValueExpenseReview
                  ? "High value expense (>10,000) requires manager approval"
                  : "Match transaction requires review",
            field_changes: fieldChanges,
            raw_json: requestBody,
          };

          // Show review modal
          setPendingMatchAction({
            action: async () => {
              // After review is approved, retry the match
              await handleMatch();
            },
            changeDetails,
            actionCode: primaryActionCode,
          });
          setIsReviewModalOpen(true);
          setIsMatching(false);
          return;
        }
      }

      // Handle different categories with different API calls
      if (selectedCategory === "Vendor Payment") {
        // Check permissions
        if (hasMultipleVendors && !canSplitTransaction) {
          showError("You do not have permission to split transactions");
          setIsMatching(false);
          return;
        }

        if (totalAdvance > 0 && !canAdvance) {
          showError("You do not have permission to process advance payments");
          setIsMatching(false);
          return;
        }

        if (!canMatch) {
          showError("You do not have permission to match transactions");
          setIsMatching(false);
          return;
        }

        // Handle single instance (existing behavior)
        if (vendorPaymentInstances.length === 1) {
          const instance = vendorPaymentInstances[0];

          if (!instance.vendorId || !instance.vendorName) {
            showError("Please select a vendor.");
            setFieldError("vendor", "Please select a vendor");
            setIsMatching(false);
            return;
          }

          const selectedBillsWithAmounts = instance.selectedBills.filter(
            (item) => item.isSelected && item.amount > 0,
          );
          const selectedBillsWithoutAmounts = instance.selectedBills.filter(
            (item) => item.isSelected && item.amount <= 0,
          );

          if (selectedBillsWithoutAmounts.length > 0) {
            showWarning(
              "Please enter amounts for all selected bills before matching.",
            );
            setFieldError(
              "bills",
              "Please enter amounts for all selected bills",
            );
            setIsMatching(false);
            return;
          }

          // Check for foreign currency
          const hasForeign = instance.selectedBills.some(
            (item) =>
              item.bill.currency_code && item.bill.currency_code !== "INR",
          );

          // Validate bank exchange rate for foreign currency transactions
          if (hasForeign) {
            if (!instance.bankExchangeRate || instance.bankExchangeRate <= 0) {
              showWarning(
                "Please enter the Bank Exchange Rate for foreign currency transactions.",
              );
              setFieldError(
                "bankExchangeRate",
                "Bank Exchange Rate is required for foreign currency transactions",
              );
              setIsMatching(false);
              return;
            }
          }

          const isForeign =
            hasForeign &&
            instance.bankExchangeRate &&
            instance.bankExchangeRate > 0;

          const appliedAmountFC = selectedBillsWithAmounts.reduce(
            (sum, item) => sum + item.amount,
            0,
          );
          const foreignForex = isForeign
            ? calculateSingleForeignPaymentForex(
                appliedAmountFC,
                instance.bankExchangeRate!,
                transactionData.amount,
              )
            : null;
          const amountForRequest = foreignForex
            ? foreignForex.amountForZoho
            : roundTo2Decimals(transactionData.amount);

          const requestBody: any = {
            category_type: "vendor_payment",
            transaction_id: transactionData.transaction_id || "",
            vendor_name: instance.vendorName,
            advance_payment: instance.advancePayment,
            on_account_amount: instance.onAccountAmount,
            categorize_in_zoho_json: {
              vendor_id: instance.vendorId,
              date: transactionData.date,
              bills: selectedBillsWithAmounts.map((item) => ({
                bill_id: item.bill.bill_id,
                bill_number: item.bill.bill_number || item.bill.bill_id,
                amount_applied: roundTo2Decimals(item.amount),
                adjustment_amount: item.adjustmentAmount || 0,
                total: item.bill.total,
                balance: item.bill.balance,
                status: item.bill.status,
                currency_code: item.bill.currency_code,
              })),
              amount: amountForRequest,
              paid_through_account_id: zoho_bankaccountid,
            },
          };

          if (isForeign && foreignForex) {
            requestBody.categorize_in_zoho_json.exchange_rate =
              foreignForex.exchangeRateForZoho;
            requestBody.categorize_in_zoho_json.bank_charges =
              foreignForex.bankChargesFC;
          }

          const response = await categorizeVendorTransaction(requestBody);

          if (response.success) {
            showSuccess("Vendor Payment transaction matched successfully!");
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(`Failed to categorize transaction: ${response.message}`);
            setFieldError(
              "api",
              response.message || "Failed to categorize transaction",
            );
          }
        } else {
          // Handle multiple instances
          // Validate that all instances have vendors selected
          const instancesWithoutVendors = vendorPaymentInstances.filter(
            (instance) => !instance.vendorId || !instance.vendorName,
          );
          if (instancesWithoutVendors.length > 0) {
            showWarning("Please select a vendor for all instances.");
            setFieldError("vendors", "Please select vendors for all instances");
            setIsMatching(false);
            return;
          }

          // Validate that all selected bills have amounts
          const instancesWithIncompleteBills = vendorPaymentInstances.filter(
            (instance) => {
              const selectedWithoutAmounts = instance.selectedBills.filter(
                (item) => item.isSelected && item.amount <= 0,
              );
              return selectedWithoutAmounts.length > 0;
            },
          );
          if (instancesWithIncompleteBills.length > 0) {
            showWarning(
              "Please enter amounts for all selected bills before matching.",
            );
            setFieldError(
              "bills",
              "Please enter amounts for all selected bills",
            );
            setIsMatching(false);
            return;
          }

          // Validate bank exchange rates for foreign currency instances
          const instancesWithForeignCurrency = vendorPaymentInstances.filter(
            (instance) => {
              const hasForeign = instance.selectedBills.some(
                (item) =>
                  item.bill.currency_code && item.bill.currency_code !== "INR",
              );
              return (
                hasForeign &&
                (!instance.bankExchangeRate || instance.bankExchangeRate <= 0)
              );
            },
          );
          if (instancesWithForeignCurrency.length > 0) {
            showWarning(
              "Please enter the Bank Exchange Rate for all foreign currency instances.",
            );
            setFieldError(
              "bankExchangeRate",
              "Bank Exchange Rate is required for foreign currency transactions",
            );
            setIsMatching(false);
            return;
          }

          // Validate exact match requirement
          if (!validateMatchRequirements()) {
            const totalAmounts = calculateTotalBillAmounts();
            const totalAdvance = calculateTotalAdvancePayments();
            const totalOnAccount = calculateTotalOnAccountAmounts();
            const total = totalAmounts + totalAdvance + totalOnAccount;
            const transactionAmountValue = transactionData.amount;
            showWarning(
              `Total allocated amount (bills + adjustments + advance payments) must equal the transaction amount. Current total: ${total.toFixed(
                2,
              )}, Transaction amount: ${transactionAmountValue.toFixed(
                2,
              )}. Please adjust bill amounts, adjustments, or advance payments.`,
            );
            setFieldError(
              "match",
              "Total allocated amount must equal transaction amount",
            );
            setIsMatching(false);
            return;
          }

          // Build data structure for multiple instances
          const vendors = vendorPaymentInstances.map((instance) => {
            const selectedBillsWithAmounts = instance.selectedBills.filter(
              (item) => item.isSelected && item.amount > 0,
            );

            // Check if this instance has foreign currency bills
            const hasForeign = instance.selectedBills.some(
              (item) =>
                item.isSelected &&
                item.bill.currency_code &&
                item.bill.currency_code !== "INR",
            );

            // Calculate bank charges for foreign currency transactions
            let exchangeRate: number | null = null;
            let bankCharges: number | null = null;

            if (
              hasForeign &&
              instance.bankExchangeRate &&
              instance.bankExchangeRate > 0
            ) {
              exchangeRate =
                Math.trunc(instance.bankExchangeRate * 1000000) / 1000000;

              // Calculate bank charges for this vendor
              // Realisation in INR for foreign currency bills
              const realisationINR = selectedBillsWithAmounts.reduce(
                (sum, item) => sum + item.amount * instance.bankExchangeRate!,
                0,
              );

              // This vendor's allocated portion of transaction amount (bills + advance)
              // Note: Vendors do NOT have TDS, so calculation is simpler
              const vendorAllocatedINR = selectedBillsWithAmounts.reduce(
                (sum, item) => {
                  const billAmountINR =
                    item.amount * instance.bankExchangeRate!;
                  return sum + billAmountINR;
                },
                instance.advancePayment + instance.onAccountAmount, // leftover is already in INR
              );

              // Bank charges = difference between realisation and allocated amount
              const calculatedBankCharges = Math.abs(
                realisationINR - vendorAllocatedINR,
              );

              if (calculatedBankCharges > 0.01) {
                bankCharges =
                  Math.trunc(
                    (calculatedBankCharges / instance.bankExchangeRate!) * 100,
                  ) / 100;
              }
            }

            return {
              vendor_id: instance.vendorId!,
              vendor_name: instance.vendorName!,
              advance_payment: instance.advancePayment,
              on_account_amount: instance.onAccountAmount,
              exchange_rate: exchangeRate,
              bank_charges: bankCharges,
              bills: selectedBillsWithAmounts.map((item) => ({
                bill_id: item.bill.bill_id,
                bill_number: item.bill.bill_number || item.bill.bill_id,
                amount_applied: roundTo2Decimals(item.amount),
                adjustment_amount: item.adjustmentAmount || 0,
                total: item.bill.total,
                balance: item.bill.balance,
                currency_code: item.bill.currency_code,
                currency_symbol: item.bill.currency_symbol,
                status: item.bill.status,
              })),
            };
          });

          // Build request body for multiple vendors
          const requestBody = {
            transaction_id: transactionData.transaction_id || "",
            vendors: vendors,
            date: transactionData.date,
            account_id: zoho_bankaccountid!,
            category_type: "split_vendor",
          };

          try {
            const response =
              await categorizeMultipleVendorsTransaction(requestBody);

            if (response.success) {
              if (onTransactionMatched && transactionData.transaction_id) {
                onTransactionMatched(
                  transactionData.transaction_id,
                  transactionData.amount,
                );
              }
              handleClose();
            } else {
              showError(
                `Failed to categorize transaction: ${response.message}`,
              );
              setFieldError(
                "api",
                response.message || "Failed to categorize transaction",
              );
            }
          } catch (error: any) {
            showError(
              `Failed to categorize transaction: ${
                error.message || "Unknown error occurred"
              }`,
            );
            setFieldError(
              "api",
              error.message || "Failed to categorize transaction",
            );
          } finally {
            setIsMatching(false);
          }
        }
      } else if (selectedCategory === "Transfer To Another Account") {
        // Validate Transfer specific requirements
        if (!transferToAccountId) {
          showWarning("Please select the 'To Account'.");
          setFieldError(
            "transferToAccountId",
            "Please select the 'To Account'",
          );
          setIsMatching(false);
          return;
        }

        if (!transferDate) {
          showWarning("Please enter the transfer date.");
          setFieldError("transferDate", "Please enter the transfer date");
          setIsMatching(false);
          return;
        }

        // Build Transfer request body using buildRequestBodyForCategory
        const transferRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending vendor transfer request:",
          JSON.stringify(transferRequestBody, null, 2),
        );

        const response = await categorizeVendorTransfer(transferRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize transfer: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize transfer",
          );
        }
      } else if (selectedCategory === "Transfer to Other Accounts") {
        if (isTransferSplitMode) {
          // Validate split transfer requirements
          if (!transferToOtherAccountsDate) {
            showWarning("Please enter the date.");
            setFieldError(
              "transferToOtherAccountsDate",
              "Please enter the date",
            );
            setIsMatching(false);
            return;
          }

          if (!transferSplits || transferSplits.length === 0) {
            showWarning("Please add at least one split payment.");
            setIsMatching(false);
            return;
          }

          const invalidSplits = transferSplits.filter(
            (split) =>
              !split.to_account_id ||
              !split.amount ||
              split.amount <= 0 ||
              !split.payment_mode,
          );

          if (invalidSplits.length > 0) {
            showWarning(
              "Please fill in all required fields for each split payment.",
            );
            setIsMatching(false);
            return;
          }

          const totalSplitAmount = transferSplits.reduce(
            (sum, split) => sum + (split.amount || 0),
            0,
          );
          const transactionAmount = transactionData?.amount || 0;
          const difference = Math.abs(totalSplitAmount - transactionAmount);

          if (difference >= 0.01) {
            showWarning(
              `Total split amount (₹${totalSplitAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}) does not match transaction amount (₹${transactionAmount.toLocaleString(
                "en-IN",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}).`,
            );
            setIsMatching(false);
            return;
          }

          const transferSplitRequestBody =
            buildRequestBodyForCategory(selectedCategory);

          const response = await categorizeVendorTransferToOtherAccountsSplit(
            transferSplitRequestBody,
          );

          if (response.success) {
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(
              `Failed to categorize transfer to other accounts (split): ${
                response.message || "Unknown error"
              }`,
            );
            setFieldError(
              "api",
              response.message ||
                "Failed to categorize transfer to other accounts (split)",
            );
          }
        } else {
          // Validate single Transfer to Other Accounts
          if (!transferToOtherAccountsToAccountId) {
            showWarning("Please select the 'To Account'.");
            setFieldError(
              "transferToOtherAccountsToAccountId",
              "Please select the 'To Account'",
            );
            setIsMatching(false);
            return;
          }

          if (!transferToOtherAccountsDate) {
            showWarning("Please enter the date.");
            setFieldError(
              "transferToOtherAccountsDate",
              "Please enter the date",
            );
            setIsMatching(false);
            return;
          }

          const transferToOtherAccountsRequestBody =
            buildRequestBodyForCategory(selectedCategory);

          const response = await categorizeVendorTransferToOtherAccounts(
            transferToOtherAccountsRequestBody,
          );

          if (response.success) {
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(
              `Failed to categorize transfer to other accounts: ${
                response.message || "Unknown error"
              }`,
            );
            setFieldError(
              "api",
              response.message ||
                "Failed to categorize transfer to other accounts",
            );
          }
        }
      } else if (selectedCategory === "Owner Drawing") {
        // Validate Owner Drawing specific requirements
        if (!ownerDrawingToAccount) {
          showWarning("Please select the 'To Account'.");
          setFieldError(
            "ownerDrawingToAccount",
            "Please select the 'To Account'",
          );
          setIsMatching(false);
          return;
        }

        if (!ownerDrawingDate) {
          showWarning("Please enter the date.");
          setFieldError("ownerDrawingDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        // Build Owner Drawing request body using buildRequestBodyForCategory
        const ownerDrawingRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending owner drawing request:",
          JSON.stringify(ownerDrawingRequestBody, null, 2),
        );

        const response = await categorizeOwnerDrawing(ownerDrawingRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize owner drawing: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize owner drawing",
          );
        }
      } else if (selectedCategory === "Customer Payment Refund") {
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        // Validate Customer Payment Refund specific requirements
        if (!customerRefundCustomer) {
          showWarning("Please select a customer.");
          setFieldError("customerRefundCustomerId", "Please select a customer");
          setIsMatching(false);
          return;
        }

        if (!customerRefundDate) {
          showWarning("Please enter the date.");
          setFieldError("customerRefundDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        if (
          isTallyBackend &&
          !customerRefundReferenceNumber?.trim()
        ) {
          showWarning("Please enter a Reference #.");
          setFieldError(
            "customerRefundReferenceNumber",
            "Reference # is required for Tally",
          );
          setIsMatching(false);
          return;
        }

        if (!isTallyBackend && !customerRefundSelectedPayment) {
          showWarning("Please select a payment with an amount.");
          setFieldError(
            "customerRefundSelectedPayment",
            "Please select a payment",
          );
          setIsMatching(false);
          return;
        }

        // Build Customer Payment Refund request body using buildRequestBodyForCategory
        const customerRefundRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending customer payment refund request:",
          JSON.stringify(customerRefundRequestBody, null, 2),
        );

        const response = await categorizeCustomerPaymentRefund(
          customerRefundRequestBody,
        );

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize customer payment refund: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize customer payment refund",
          );
        }
      } else if (selectedCategory === "Card Payment") {
        // Validate Card Payment specific requirements
        if (!cardPaymentToAccountId) {
          showWarning("Please select the 'To Account' (Credit Card).");
          setFieldError(
            "cardPaymentToAccountId",
            "Please select the 'To Account' (Credit Card)",
          );
          setIsMatching(false);
          return;
        }

        if (!cardPaymentDate) {
          showWarning("Please enter the date.");
          setFieldError("cardPaymentDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        // Build Card Payment request body using buildRequestBodyForCategory
        const cardPaymentRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending card payment request:",
          JSON.stringify(cardPaymentRequestBody, null, 2),
        );

        const response = await categorizeVendorCardPayment(
          cardPaymentRequestBody,
        );

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize card payment: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize card payment",
          );
        }
      } else if (selectedCategory === "Expense") {
        if (!selectedExpenseGlAccountId) {
          showWarning("Please select an expense GL.");
          setFieldError("expenseGl", "Please select an expense GL");
          setIsMatching(false);
          return;
        }

        // Check permission for high-value expense
        const expenseAmount = transactionData?.amount || 0;
        if (expenseAmount > 10000 && !canProcessHighValueExpense) {
          showError(
            "You do not have permission to process high-value expenses",
          );
          setIsMatching(false);
          return;
        }

        // Build Expense request body using buildRequestBodyForCategory
        const expenseRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        const response = await categorizeVendorExpense(expenseRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize vendor expense: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize vendor expense",
          );
        }
      }
    } catch (error) {
      console.error("Error processing transaction:", error);
      const userFriendlyError = extractErrorMessage(error);
      showError(`Failed to process transaction: ${userFriendlyError}`);
      setApiErrors(error);
    } finally {
      setIsMatching(false);
    }
  };

  // Check if there are validation errors
  const hasValidationErrors = () => {
    if (selectedCategory === "Vendor Payment") {
      // For multiple instances, check validation
      if (vendorPaymentInstances.length > 1) {
        // Check if all instances have vendors
        const instancesWithoutVendors = vendorPaymentInstances.filter(
          (instance) => !instance.vendorId || !instance.vendorName,
        );
        if (instancesWithoutVendors.length > 0) {
          return true;
        }

        // Check if exact match requirement is met
        return !validateMatchRequirements();
      } else {
        // Single instance validation
        const instance = vendorPaymentInstances[0];
        if (!instance.vendorId || !instance.vendorName) {
          return true;
        }

        // Validate that selected bills have amounts entered
        const selectedBillsWithoutAmounts = instance.selectedBills.filter(
          (item) => item.isSelected && item.amount <= 0,
        );
        return selectedBillsWithoutAmounts.length > 0;
      }
    } else if (selectedCategory === "Transfer To Another Account") {
      // Validate required fields for transfer (transaction existence is optional)
      // return !transferToAccountId || !transferDate;
      return (
        !transferToAccountId || !transferDate || !transferTransactionExists
      );
    } else if (selectedCategory === "Transfer to Other Accounts") {
      if (isTransferSplitMode) {
        if (!transferToOtherAccountsDate) return true;
        if (!transferSplits || transferSplits.length === 0) return true;
        const hasInvalidSplit = transferSplits.some(
          (split) =>
            !split.to_account_id ||
            !split.amount ||
            split.amount <= 0 ||
            !split.payment_mode,
        );
        if (hasInvalidSplit) return true;
        const totalSplitAmount = transferSplits.reduce(
          (sum, split) => sum + (split.amount || 0),
          0,
        );
        const transactionAmount = transactionData?.amount || 0;
        return Math.abs(totalSplitAmount - transactionAmount) >= 0.01;
      }
      return (
        !transferToOtherAccountsToAccountId || !transferToOtherAccountsDate
      );
    } else if (selectedCategory === "Owner Drawing") {
      // Validate required fields for owner drawing
      return !ownerDrawingToAccount || !ownerDrawingDate;
    } else if (selectedCategory === "Customer Payment Refund") {
      // Validate required fields for customer payment refund
      if (!customerRefundCustomer || !customerRefundDate) {
        return true;
      }
      if (getStoredAccountingBackend() === "tally") {
        return !customerRefundReferenceNumber?.trim();
      }
      // Zoho: payment selection required
      return !customerRefundSelectedPayment;
    } else if (selectedCategory === "Card Payment") {
      // Validate required fields for card payment
      return !cardPaymentToAccountId || !cardPaymentDate;
    } else if (selectedCategory === "Expense") {
      return !selectedExpenseGlAccountId;
    }

    return false;
  };

  const handleCancel = () => {
    handleClose();
  };

  // Old handler functions removed - logic moved to VendorPaymentReco component

  // Helper function to check if selected category matches the zoho_category from transaction
  const shouldUseZohoGl = (category: string): boolean => {
    const categoryToZohoMap: Record<string, string> = {
      "Vendor Payment": "vendor_payment",
      Expense: "expense",
      "Transfer To Another Account": "transfer_to_another_account",
      "Transfer to Other Accounts": "transfer_to_other_accounts",
      "Owner Drawing": "owner_drawing",
      "Customer Payment Refund": "customer_payment_refund",
      "Card Payment": "card_payment",
    };

    const expectedZohoCategory = categoryToZohoMap[category];
    const actualZohoCategory =
      transactionData?.zoho_category || transactionData?.category_type;

    return expectedZohoCategory === actualZohoCategory;
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCategoryFromSuggestion(false); // User changed; no longer showing recommendation
    setSelectedExpenseGlAccountId("");
    setSelectedExpenseGlName("");
    setFormChanges((prev) => ({
      ...prev,
      category,
    }));
  };

  // Old handler functions removed - logic moved to VendorPaymentReco component

  // Don't render if no transaction data
  if (!transactionData) {
    return null;
  }

  return (
    <DialogLayout open={open} onOpenChange={handleOpenChange}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Review Transaction
        </span>
        {checkHasErrors() && (
          <ErrorBanner
            errors={errors}
            title="Please fix the following errors:"
          />
        )}
        {isMonthClosed === true && <ClosedMonthBanner />}
        <div className="flex w-full flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-3">
                <IconWithBackground icon={<FeatherCreditCard />} />
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {transactionData.description}
                </span>
              </div>
              <span className="text-heading-3 font-heading-3 text-error-600">
                -{transactionData.currency_code || "INR"}{" "}
                {transactionData.amount.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex w-full flex-wrap items-center gap-6 border-t border-solid border-neutral-border pt-4">
              <div className="flex items-center gap-2">
                <FeatherCalendar className="text-body font-body text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  {new Date(transactionData.date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <FeatherActivity className="text-body font-body text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  Withdrawal
                </span>
              </div>
              <Badge
                variant={
                  reviewStatus === "rejected"
                    ? "error"
                    : transactionData.user_status === "Reconciled"
                      ? "success"
                      : "warning"
                }
                className={
                  reviewStatus === "pending"
                    ? "border border-solid border-purple-200 bg-purple-50 [&>span]:text-purple-700"
                    : undefined
                }
              >
                {reviewStatus === "rejected"
                  ? "Rejected"
                  : reviewStatus === "pending"
                    ? "Sent for Review"
                    : transactionData.user_status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Show rejection reason if review is rejected */}
        {reviewStatus === "rejected" &&
          reviewForPopulation?.review_comments && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-error-50 px-4 py-3 border border-solid border-error-200">
              <span className="text-body-bold font-body-bold text-error-700">
                Review Rejected
              </span>
              <span className="text-body font-body text-error-700">
                {reviewForPopulation.review_comments}
              </span>
            </div>
          )}

        {/* Review Status Banner for Pending */}
        {reviewStatus === "pending" && reviewForPopulation && (
          <div className="flex w-full items-center gap-3 rounded-lg border border-warning-300 bg-warning-50 px-4 py-3">
            <span className="text-body text-warning-700">
              This transaction has a pending review request for{" "}
              {isSplitTransactionAmountReview(reviewForPopulation)
                ? "Split Transaction Amount"
                : reviewForPopulation.change_details?.raw_json
                      ?.category_type === "expense"
                  ? "Expense type as it is >10K"
                  : reviewForPopulation.change_details?.raw_json
                      ?.category_type || "this action"}
              . Please wait for manager approval.
            </span>
          </div>
        )}

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {isViewOnlyMode() ? "Review Data" : "Manual Categorization"}
            </span>
            {selectedCategory === "Vendor Payment" && !isViewOnlyMode() && (
              <Button
                variant="brand-secondary"
                size="small"
                icon={<FeatherPlus />}
                onClick={handleAddVendor}
              >
                Add Vendor
              </Button>
            )}
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            {isWaitingForReviewData ? (
              <div className="flex w-full items-center rounded-md border border-solid border-neutral-border bg-default-background px-6 py-4">
                <span className="text-body font-body text-subtext-color">
                  {isReviewDataLoading
                    ? "Loading review data..."
                    : "Review data is not available yet."}
                </span>
              </div>
            ) : isViewOnlyMode() &&
              isSplitTransactionAmountReview(reviewForPopulation) ? (
              <SplitTransactionAmountReviewView
                rawJson={getReviewRawJson()}
                fallbackAmount={transactionData?.amount}
                fallbackCurrency={transactionData?.currency_symbol}
                fallbackDescription={transactionData?.description}
              />
            ) : (
              <>
                <div className="flex w-full items-center gap-16">
                  <div className="flex items-center gap-2">
                    <span className="text-body font-body text-default-font">
                      Category
                    </span>
                    {isViewOnlyMode() ? (
                      <span className="text-body-bold font-body-bold text-default-font">
                        {(() => {
                          const categoryType =
                            reviewForPopulation?.change_details?.raw_json
                              ?.category_type;
                          if (categoryType === "expense") return "Expense";
                          if (
                            categoryType === "vendor_payment" ||
                            categoryType === "split_vendor"
                          )
                            return "Vendor Payment";
                          return selectedCategory;
                        })()}
                      </span>
                    ) : (
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <Button
                            className="h-8 w-40 flex-none justify-between"
                            variant="neutral-secondary"
                            iconRight={
                              suggestionApplying || isPopulatingFromReview ? (
                                <FeatherLoader className="animate-spin" />
                              ) : (
                                <FeatherChevronDown />
                              )
                            }
                            disabled={
                              suggestionApplying || isPopulatingFromReview
                            }
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {}}
                          >
                            {suggestionApplying || isPopulatingFromReview
                              ? "Loading..."
                              : truncateText(selectedCategory, 15)}
                          </Button>
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="start"
                            sideOffset={4}
                            asChild={true}
                          >
                            <div className="max-h-60 overflow-y-auto dropdown-menu-content">
                              <DropdownMenu>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherDollarSign />}
                                  onClick={() =>
                                    handleCategorySelect("Vendor Payment")
                                  }
                                >
                                  Vendor Payment
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherDollarSign />}
                                  onClick={() =>
                                    handleCategorySelect("Expense")
                                  }
                                >
                                  Expense
                                </DropdownMenu.DropdownItem>

                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Transfer To Another Account",
                                    )
                                  }
                                >
                                  Transfer To Another Account
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Transfer to Other Accounts",
                                    )
                                  }
                                >
                                  Transfer to Other Accounts
                                </DropdownMenu.DropdownItem>
                                {getStoredAccountingBackend() !== "tally" && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherDollarSign />}
                                    onClick={() =>
                                      handleCategorySelect("Owner Drawing")
                                    }
                                  >
                                    Owner Drawing
                                  </DropdownMenu.DropdownItem>
                                )}
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Customer Payment Refund",
                                    )
                                  }
                                >
                                  Customer Payment Refund
                                </DropdownMenu.DropdownItem>
                                {getStoredAccountingBackend() !== "tally" && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherCreditCard />}
                                    onClick={() =>
                                      handleCategorySelect("Card Payment")
                                    }
                                  >
                                    Card Payment
                                  </DropdownMenu.DropdownItem>
                                )}
                              </DropdownMenu>
                            </div>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    )}
                    {!isViewOnlyMode() &&
                      categoryFromSuggestion &&
                      hasSuggestedCategory &&
                      !(
                        suggestedCategoryValue === "vendor_payment" &&
                        transactionData?.zoho_partyid === "create_new"
                      ) && <Badge variant="brand">Recommended</Badge>}
                  </div>

                  {selectedCategory === "Expense" && !isViewOnlyMode() && (
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-default-font">
                        Expense Account
                      </span>
                      <BankGlAccountDropdown
                        preset="expense"
                        selectedAccountId={selectedExpenseGlAccountId}
                        selectedAccountName={selectedExpenseGlName}
                        onSelect={(account) => {
                          setSelectedExpenseGlAccountId(
                            String(account.account_id),
                          );
                          setSelectedExpenseGlName(account.gl_name);
                        }}
                        placeholder="Select the expense account"
                        dropdownClassName="min-w-[18rem]"
                        buttonClassName="h-8 w-72 min-w-[18rem] flex-none overflow-hidden"
                      />
                    </div>
                  )}

                  {/* <div className="flex grow shrink-0 basis-0 items-center justify-end gap-1 self-stretch">
                <IconButton
                  size="small"
                  icon={<FeatherTrash />}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                />
              </div> */}
                </div>

                {selectedCategory === "Vendor Payment" && (
                  <>
                    {isViewOnlyMode() ? (
                      // View-only mode: Show data from raw_json
                      renderVendorPaymentView()
                    ) : (
                      // Normal mode: Show form components
                      <>
                        {getStoredAccountingBackend() === "tally" && (
                          <Checkbox
                            label="Knock off opening balance"
                            checked={knockOffOpeningBalance}
                            onCheckedChange={(checked) =>
                              setKnockOffOpeningBalance(checked === true)
                            }
                          />
                        )}
                        {/* Render all vendor payment instances */}
                        {vendorPaymentInstances.map((instance, index) => {
                          const totalFromOthers =
                            calculateTotalAllocatedAmountsFromOtherInstances(
                              instance.id,
                            );
                          // Get excluded vendor IDs (all other selected vendors)
                          const excludedVendorIds = vendorPaymentInstances
                            .filter(
                              (inst) =>
                                inst.id !== instance.id && inst.vendorId,
                            )
                            .map((inst) => inst.vendorId!);
                          return (
                            <VendorPaymentReco
                              key={instance.id}
                              id={instance.id}
                              excludedVendorIds={excludedVendorIds}
                              ref={setInstanceRef(instance.id)}
                              transactionAmount={transactionData?.amount || 0}
                              currencySymbol={
                                instance.selectedBills[0]?.bill
                                  .currency_symbol ||
                                transactionData?.currency_symbol ||
                                "₹"
                              }
                              bankExchangeRate={instance.bankExchangeRate}
                              onBankExchangeRateChange={(rate) => {
                                handleInstanceChange(instance.id, {
                                  vendorId: instance.vendorId,
                                  vendorName: instance.vendorName,
                                  selectedBills: instance.selectedBills,
                                  advancePayment: instance.advancePayment,
                                  onAccountAmount: instance.onAccountAmount,
                                  totalBillAmounts: instance.totalBillAmounts,
                                  bankExchangeRate: rate,
                                });
                              }}
                              showRemoveButton={
                                vendorPaymentInstances.length > 1
                              }
                              onRemove={
                                vendorPaymentInstances.length > 1
                                  ? () => handleRemoveVendor(instance.id)
                                  : undefined
                              }
                              isMultipleInstances={
                                vendorPaymentInstances.length > 1
                              }
                              totalAllocatedAmountsFromOtherInstances={
                                totalFromOthers
                              }
                              knockOffOpeningBalance={knockOffOpeningBalance}
                              onChange={getInstanceChangeCallback(instance.id)}
                              autoSelectVendorId={
                                index === 0 &&
                                transactionData?.zoho_partyid &&
                                transactionData?.zoho_partyid !== "create_new"
                                  ? transactionData.zoho_partyid
                                  : undefined
                              }
                              autoSelectVendorName={
                                index === 0 &&
                                transactionData?.zoho_partyid_confidence
                                  ? transactionData.description || undefined
                                  : undefined
                              }
                              recommendationSource={
                                index === 0
                                  ? transactionData?.zoho_partyid_source
                                  : undefined
                              }
                              recommendationConfidence={
                                index === 0
                                  ? transactionData?.zoho_partyid_confidence
                                  : undefined
                              }
                              onInitialPartyLoaded={
                                index === 0 &&
                                selectedCategory === "Vendor Payment" &&
                                hasSuggestedParty
                                  ? () => setInitialPartyLoaded(true)
                                  : undefined
                              }
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                )}

                {selectedCategory === "Vendor Advance" && (
                  <div className="flex w-full flex-col items-start gap-2">
                    <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                      <div className="flex items-center gap-4">
                        <span className="text-body font-body text-default-font">
                          This will be recorded as an advance payment to the
                          vendor
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedCategory === "Transfer To Another Account" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <VendorTransferToAccountReco
                      currentBankAccountId={currentBankAccountId}
                      currentBankAccountName={currentBankAccountName}
                      allBankAccounts={allBankAccounts}
                      transactionDate={transactionData?.date || ""}
                      transactionAmount={transactionData?.amount || 0}
                      currentTransactionId={
                        transactionData?.transaction_id || ""
                      }
                      onToAccountChange={setTransferToAccountId}
                      onDateChange={setTransferDate}
                      onReferenceNumberChange={setTransferReferenceNumber}
                      onTransactionExistsChange={setTransferTransactionExists}
                      onToTransactionIdChange={setTransferToTransactionId}
                      onToAccountZohoBankAccountIdChange={
                        setTransferToAccountZohoBankAccountId
                      }
                      zohoGl={
                        shouldUseZohoGl("Transfer To Another Account")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Transfer to Other Accounts" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <div className="flex w-full flex-col items-start gap-4">
                      <div className="flex w-full items-center justify-end">
                        <Button
                          variant="neutral-secondary"
                          size="small"
                          onClick={() =>
                            setIsTransferSplitMode(!isTransferSplitMode)
                          }
                        >
                          {isTransferSplitMode
                            ? "Single Payment"
                            : "Split Payment"}
                        </Button>
                      </div>
                      {isTransferSplitMode ? (
                        <VendorTransferToOtherAccountsSplitReco
                          transactionDate={transactionData?.date || ""}
                          transactionAmount={transactionData?.amount || 0}
                          bankAccountId={zoho_bankaccountid || undefined}
                          onDateChange={setTransferToOtherAccountsDate}
                          onReferenceNumberChange={
                            setTransferToOtherAccountsReferenceNumber
                          }
                          onSplitsChange={setTransferSplits}
                          zohoGl={
                            shouldUseZohoGl("Transfer to Other Accounts")
                              ? transactionData?.zoho_gl
                              : undefined
                          }
                        />
                      ) : (
                        <VendorTransferToOtherAccountsReco
                          transactionDate={transactionData?.date || ""}
                          bankAccountId={zoho_bankaccountid || undefined}
                          onDateChange={setTransferToOtherAccountsDate}
                          onPaymentModeChange={
                            setTransferToOtherAccountsPaymentMode
                          }
                          onToAccountChange={
                            setTransferToOtherAccountsToAccountId
                          }
                          onReferenceNumberChange={
                            setTransferToOtherAccountsReferenceNumber
                          }
                          zohoGl={
                            shouldUseZohoGl("Transfer to Other Accounts")
                              ? transactionData?.zoho_gl
                              : undefined
                          }
                        />
                      )}
                    </div>
                  ))}

                {selectedCategory === "Owner Drawing" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <VendorOwnerDrawingReco
                      transactionDate={transactionData?.date || ""}
                      onToAccountChange={setOwnerDrawingToAccount}
                      onDateChange={setOwnerDrawingDate}
                      onPaymentModeChange={setOwnerDrawingPaymentMode}
                      onReferenceNumberChange={setOwnerDrawingReferenceNumber}
                      zohoGl={
                        shouldUseZohoGl("Owner Drawing")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Customer Payment Refund" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <VendorCustomerPaymentRefundReco
                      transactionDate={transactionData?.date || ""}
                      transactionAmount={transactionData?.amount || 0}
                      onCustomerChange={setCustomerRefundCustomer}
                      onDateChange={setCustomerRefundDate}
                      onReferenceNumberChange={setCustomerRefundReferenceNumber}
                      onPaymentModeChange={setCustomerRefundPaymentMode}
                      onSelectedPaymentsChange={
                        setCustomerRefundSelectedPayment
                      }
                    />
                  ))}

                {selectedCategory === "Card Payment" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <VendorCardPaymentReco
                      currentBankAccountName={currentBankAccountName}
                      creditCardAccounts={creditCardAccounts}
                      transactionDate={transactionData?.date || ""}
                      onToAccountChange={setCardPaymentToAccountId}
                      onDateChange={setCardPaymentDate}
                      onReferenceNumberChange={setCardPaymentReferenceNumber}
                    />
                  ))}

                {selectedCategory === "Expense" &&
                  (isViewOnlyMode() ? renderOtherCategoryView() : null)}
              </>
            )}
          </div>
          {!isViewOnlyMode() && (
            <div className="flex w-full flex-col items-start gap-4 rounded-md bg-neutral-50 px-6 py-4">
              <div className="flex w-full items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  Amount Paid
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {vendorPaymentInstances[0]?.selectedBills[0]?.bill
                    .currency_symbol ||
                    transactionData?.currency_symbol ||
                    "₹"}
                  {formatAmount(transactionData.amount)}
                </span>
              </div>

              {selectedCategory === "Vendor Payment" && (
                <>
                  {/* Warning when totals don't match (only show, don't scroll) */}
                  {vendorPaymentInstances.length > 1 &&
                    !validateMatchRequirements() && (
                      <div className="w-full rounded-md border border-solid border-error-300 bg-error-50 px-4 py-3">
                        <div className="flex items-start gap-2">
                          <span className="text-body font-body text-error-600">
                            ⚠️ Total allocated amount (bills + advance payments)
                            does not equal the transaction amount. Please adjust
                            bill amounts or advance payments.
                          </span>
                        </div>
                      </div>
                    )}
                  {(() => {
                    // Calculate totals across all instances
                    const totalBillAmounts = calculateTotalBillAmounts();
                    const totalAdvancePayments =
                      calculateTotalAdvancePayments();
                    const totalOnAccountAmounts =
                      calculateTotalOnAccountAmounts();

                    const currencySymbol =
                      vendorPaymentInstances[0]?.selectedBills[0]?.bill
                        .currency_symbol ||
                      transactionData?.currency_symbol ||
                      "₹";

                    // Check if any instance has foreign currency
                    const hasAnyForeign = vendorPaymentInstances.some(
                      (instance) =>
                        instance.selectedBills.some(
                          (item) =>
                            item.bill.currency_code &&
                            item.bill.currency_code !== "INR",
                        ),
                    );

                    // Get bank exchange rate from first instance that has it (for display purposes)
                    const firstBankExchangeRate =
                      vendorPaymentInstances.find(
                        (instance) => instance.bankExchangeRate !== null,
                      )?.bankExchangeRate || null;

                    // Show per-instance breakdown if multiple instances
                    if (vendorPaymentInstances.length > 1) {
                      return (
                        <>
                          {vendorPaymentInstances.map((instance, idx) => {
                            const instanceBillAmounts =
                              instance.totalBillAmounts;
                            const instanceAdvancePayment =
                              instance.advancePayment;
                            const instanceOnAccountAmount =
                              instance.onAccountAmount;
                            const instanceCurrencySymbol =
                              instance.selectedBills[0]?.bill.currency_symbol ||
                              transactionData?.currency_symbol ||
                              "₹";

                            return (
                              <div
                                key={instance.id}
                                className="flex w-full flex-col gap-2 border-t border-solid border-neutral-border pt-3 mt-2"
                              >
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    Vendor {idx + 1}:{" "}
                                    {instance.vendorName || "Not Selected"}
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between pl-4">
                                  <span className="text-body font-body text-subtext-color">
                                    Amount used for Payments
                                  </span>
                                  <span className="text-body-bold font-body-bold text-success-600">
                                    {instanceCurrencySymbol}
                                    {formatAmount(instanceBillAmounts)}
                                  </span>
                                </div>
                                {instanceOnAccountAmount > 0 && (
                                  <div className="flex w-full items-center justify-between pl-4">
                                    <span className="text-body font-body text-subtext-color">
                                      Opening balance
                                    </span>
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      ₹{formatAmount(instanceOnAccountAmount)}
                                    </span>
                                  </div>
                                )}
                                {instanceAdvancePayment > 0 && (
                                  <div className="flex w-full items-center justify-between pl-4">
                                    <span className="text-body font-body text-subtext-color">
                                      Advance Payment
                                    </span>
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      {hasAnyForeign &&
                                      instance.bankExchangeRate &&
                                      instance.bankExchangeRate > 0
                                        ? `${instanceCurrencySymbol}${formatAmount(
                                            Math.max(0, instanceAdvancePayment),
                                          )}`
                                        : `₹${formatAmount(
                                            Math.max(0, instanceAdvancePayment),
                                          )}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      );
                    }

                    // Single instance - show aggregated totals
                    return (
                      <>
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Amount used for Payments
                          </span>
                          <span className="text-body-bold font-body-bold text-success-600">
                            {currencySymbol}
                            {formatAmount(totalBillAmounts)}
                          </span>
                        </div>

                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            {knockOffOpeningBalance
                              ? "Opening balance"
                              : "Advance Payment"}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {hasAnyForeign &&
                            firstBankExchangeRate &&
                            firstBankExchangeRate > 0
                              ? `${currencySymbol}${formatAmount(
                                  Math.max(
                                    0,
                                    knockOffOpeningBalance
                                      ? totalOnAccountAmounts
                                      : totalAdvancePayments,
                                  ),
                                )}`
                              : `₹${formatAmount(
                                  Math.max(
                                    0,
                                    knockOffOpeningBalance
                                      ? totalOnAccountAmounts
                                      : totalAdvancePayments,
                                  ),
                                )}`}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}

              {selectedCategory === "Vendor Advance" && (
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Advance Payment to Vendor
                  </span>
                  <span className="text-body-bold font-body-bold text-success-600">
                    {transactionData.currency_code || "INR"}{" "}
                    {transactionData.amount.toLocaleString("en-IN")}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Activity Logs
            </span>
          </div>
          {isLoadingActivityLogs ? (
            <div className="flex w-full items-center justify-center py-4 text-body text-subtext-color">
              Loading activity logs...
            </div>
          ) : activityLogs.length === 0 ? (
            <span className="text-body text-subtext-color">
              No activity logs available.
            </span>
          ) : (
            <div className="flex flex-col gap-3">
              {activityLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col gap-1 rounded border border-neutral-border px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold text-default-font">
                      {log.activity_name}
                    </span>
                    <span className="text-caption text-subtext-color">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-caption text-subtext-color">
                    By {log.user_name}
                  </span>
                  <span className="text-body text-default-font">
                    {log.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-6">
          {/* <Button
            variant="neutral-tertiary"
            icon={<FeatherFlag />}
            onClick={handleFlagForReview}
          >
            Flag for Review
          </Button> */}
          <div className="flex items-center gap-2">
            <Button variant="neutral-tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleMatch}
              disabled={
                isInitialLoading ||
                hasValidationErrors() ||
                isMatching ||
                reviewStatus === "pending" ||
                isViewOnlyMode()
              }
              loading={isInitialLoading || isMatching}
              className={
                isInitialLoading ||
                hasValidationErrors() ||
                reviewStatus === "pending" ||
                isViewOnlyMode()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            >
              Match
            </Button>
          </div>
        </div>
      </div>

      {/* Closed month confirm modal - when non-Admin/CFO tries to match in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={setShowClosedMonthConfirmModal}
        entityLabel="Bank transaction"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          const requestBody = buildRequestBodyForCategory(selectedCategory);
          const oldData = buildEmptyOldDataForCategory(selectedCategory);
          const fieldChanges = buildFieldChangesForCategory(
            selectedCategory,
            oldData,
            requestBody,
          );
          setPendingMatchAction({
            action: async () => {
              await handleMatch();
            },
            changeDetails: {
              closed_month: true,
              action_types: ["match", ACTION_TYPE_MONTH_CLOSE],
              field_changes: fieldChanges,
              raw_json: requestBody,
            },
            actionCode: ACTION_CODES.MATCH,
          });
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      {transactionData?.id && pendingMatchAction && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.BANK_TRANSACTION}
          actionCode={pendingMatchAction.actionCode || ACTION_CODES.MATCH}
          entityType="bank_transaction"
          entityId={transactionData.id}
          changeDetails={pendingMatchAction.changeDetails}
          transactionData={transactionData}
          skipNavigation={true}
          onSuccess={async () => {
            // ReviewRequestModal already showed success toast ("Review request submitted successfully")

            // Close the review modal (already handled by ReviewRequestModal)
            setIsReviewModalOpen(false);

            // Close the transaction modal
            handleClose();

            // Only refresh list so "Sent for Review" shows; do NOT call onTransactionMatched (that would show "reconciled successfully")
            onReviewRequestSubmitted?.();
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setApiErrors(error);
          }}
        />
      )}
    </DialogLayout>
  );
};

export default VendorPaymentTransactionModal;
